import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { IndosbookLogo } from "./IndosbookLogo";
import { useAuth, api } from "../context/AuthContext";
import { rankOptions, vesselOptions } from "../mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  ArrowLeft, Camera, Pencil, Plus, Trash2, Ship, Award, FileText,
  Loader2, Download, BadgeCheck, Anchor, CalendarDays,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const TEAL = "#002855";
const ORANGE = "#FF4500";
const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const AVAILABILITY = {
  ready: { label: "Ready to Board", cls: "bg-green-50 text-green-700", dot: "bg-green-500" },
  at_sea: { label: "At Sea", cls: "bg-blue-50 text-blue-700", dot: "bg-blue-500" },
  on_leave: { label: "On Leave", cls: "bg-gray-100 text-gray-600", dot: "bg-gray-400" },
};

const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);
const durationLabel = (signOn, signOff) => {
  if (!signOn) return "—";
  const days = daysBetween(signOn, signOff || new Date().toISOString().slice(0, 10));
  if (days < 0) return "—";
  const m = Math.floor(days / 30.44), d = Math.round(days - m * 30.44);
  return m > 0 ? `${m} mo ${d} d` : `${d} d`;
};

const certStatus = (expiry) => {
  if (!expiry) return { label: "No expiry", cls: "bg-gray-100 text-gray-600" };
  const days = daysBetween(new Date().toISOString().slice(0, 10), expiry);
  if (days < 0) return { label: "Expired", cls: "bg-red-50 text-red-700" };
  if (days <= 60) return { label: `Expires in ${days}d`, cls: "bg-amber-50 text-amber-700" };
  return { label: "Valid", cls: "bg-green-50 text-green-700" };
};

const EMPTY_SEA = { vessel_name: "", company: "", rank: "", vessel_type: "", sign_on: "", sign_off: "", grt: "", engine: "" };
const EMPTY_CERT = { name: "", cert_number: "", issue_date: "", expiry_date: "", file: null };

const ProfilePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, setUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [seaService, setSeaService] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", rank: "", bio: "", phone: "", availability: "ready" });
  const [seaOpen, setSeaOpen] = useState(false);
  const [seaForm, setSeaForm] = useState(EMPTY_SEA);
  const [seaEditId, setSeaEditId] = useState(null);
  const [certOpen, setCertOpen] = useState(false);
  const [certForm, setCertForm] = useState(EMPTY_CERT);
  const [certEditId, setCertEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const avatarInput = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/profile");
        setProfile(res.data.profile);
        setSeaService(res.data.sea_service);
        setCerts(res.data.certificates);
      } catch {
        toast({ title: "Could not load profile", variant: "destructive" });
      }
      setLoading(false);
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!loading && window.location.hash === "#vault") {
      document.getElementById("vault")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [loading]);

  const totalDays = useMemo(
    () => seaService.reduce((s, r) => s + Math.max(0, daysBetween(r.sign_on, r.sign_off || new Date().toISOString().slice(0, 10))), 0),
    [seaService]
  );
  const seaTime = `${Math.floor(totalDays / 30.44)} mo ${Math.round(totalDays % 30.44)} d`;

  const fileUrl = (c) =>
    c.storage_path ? `${API_BASE}/files/${c.storage_path}` : null;

  // ---------- profile ----------
  const openEdit = () => {
    setEditForm({
      name: profile.name || "", rank: profile.rank || "",
      bio: profile.bio || "", phone: profile.phone || "", availability: profile.availability || "ready",
    });
    setEditOpen(true);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await api.put("/profile", editForm);
      setProfile(res.data.profile);
      setUser(res.data.profile);
      setEditOpen(false);
      toast({ title: "Profile updated" });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/profile/avatar", fd);
      setProfile((p) => ({ ...p, avatar_path: res.data.avatar_path }));
      setUser((u) => ({ ...u, avatar_path: res.data.avatar_path }));
      toast({ title: "Photo updated" });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  // ---------- sea service ----------
  const openSea = (rec) => {
    setSeaEditId(rec?.id || null);
    setSeaForm(rec ? { ...EMPTY_SEA, ...rec } : EMPTY_SEA);
    setSeaOpen(true);
  };

  const saveSea = async () => {
    if (!seaForm.vessel_name.trim() || !seaForm.rank || !seaForm.sign_on) {
      toast({ title: "Vessel name, rank and sign-on date are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      if (seaEditId) {
        const res = await api.put(`/profile/sea-service/${seaEditId}`, seaForm);
        setSeaService((l) => l.map((r) => (r.id === seaEditId ? res.data : r)));
      } else {
        const res = await api.post("/profile/sea-service", seaForm);
        setSeaService((l) => [res.data, ...l]);
      }
      setSeaOpen(false);
      toast({ title: seaEditId ? "Record updated" : "Record added" });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const deleteSea = async (id) => {
    try {
      await api.delete(`/profile/sea-service/${id}`);
      setSeaService((l) => l.filter((r) => r.id !== id));
      toast({ title: "Record deleted" });
    } catch { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  // ---------- certificates ----------
  const openCert = (c) => {
    setCertEditId(c?.id || null);
    setCertForm(c ? { name: c.name, cert_number: c.cert_number || "", issue_date: c.issue_date || "", expiry_date: c.expiry_date || "", file: null } : EMPTY_CERT);
    setCertOpen(true);
  };

  const saveCert = async () => {
    if (!certForm.name.trim()) { toast({ title: "Certificate name is required", variant: "destructive" }); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", certForm.name);
      if (certForm.cert_number) fd.append("cert_number", certForm.cert_number);
      if (certForm.issue_date) fd.append("issue_date", certForm.issue_date);
      if (certForm.expiry_date) fd.append("expiry_date", certForm.expiry_date);
      if (certForm.file) fd.append("file", certForm.file);
      if (certEditId) {
        const res = await api.put(`/profile/certificates/${certEditId}`, fd);
        setCerts((l) => l.map((c) => (c.id === certEditId ? res.data : c)));
      } else {
        const res = await api.post("/profile/certificates", fd);
        setCerts((l) => [res.data, ...l]);
      }
      setCertOpen(false);
      toast({ title: certEditId ? "Certificate updated" : "Certificate added" });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const deleteCert = async (id) => {
    try {
      await api.delete(`/profile/certificates/${id}`);
      setCerts((l) => l.filter((c) => c.id !== id));
      toast({ title: "Certificate deleted" });
    } catch { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  if (loading || !profile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF4500]" />
      </div>
    );
  }

  const avail = AVAILABILITY[profile.availability] || AVAILABILITY.ready;
  const avatarSrc = profile.avatar_path ? `${API_BASE}/files/${profile.avatar_path}` : profile.picture;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white">
        <div className="mx-auto flex h-16 max-w-[1100px] items-center gap-4 px-4 sm:px-6">
          <button onClick={() => navigate("/feed")} className="shrink-0" data-testid="profile-logo-home">
            <IndosbookLogo size={28} />
          </button>
          <span className="text-sm font-bold text-gray-700">My Profile</span>
          <Button variant="outline" onClick={() => navigate("/feed")} data-testid="back-to-feed-button"
            className="ml-auto rounded-full text-sm font-bold">
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to Feed
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-[1100px] space-y-6 px-4 py-6 sm:px-6">
        {/* ===== Profile header ===== */}
        <section className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="profile-header-card">
          <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
            <div className="relative shrink-0">
              {avatarSrc ? (
                <img src={avatarSrc} alt="Profile" className="h-24 w-24 rounded-full object-cover" />
              ) : (
                <span className="flex h-24 w-24 items-center justify-center rounded-full bg-[#002855] text-3xl font-black text-white">
                  {(profile.name || "S").charAt(0).toUpperCase()}
                </span>
              )}
              <button onClick={() => avatarInput.current?.click()} data-testid="avatar-upload-button"
                className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gray-900 text-white hover:bg-gray-700">
                <Camera className="h-4 w-4" />
              </button>
              <input ref={avatarInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadAvatar} data-testid="avatar-upload-input" />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-extrabold text-gray-900" data-testid="profile-name">{profile.name}</h1>
                <BadgeCheck className="h-5 w-5 text-[#002855]" />
              </div>
              <p className="mt-0.5 text-sm font-semibold capitalize text-gray-600">
                {profile.rank || "Seafarer"}{profile.organization ? ` · ${profile.organization}` : ""}
              </p>
              <p className="text-xs text-gray-400">{profile.email}</p>
              {profile.bio && <p className="mt-2 max-w-xl text-sm text-gray-600" data-testid="profile-bio">{profile.bio}</p>}
              <span className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${avail.cls}`} data-testid="availability-badge">
                <span className={`h-2 w-2 rounded-full ${avail.dot}`} /> {avail.label}
              </span>
            </div>

            <Button onClick={openEdit} variant="outline" data-testid="profile-edit-button"
              className="rounded-full border-2 font-bold text-gray-700">
              <Pencil className="mr-1.5 h-4 w-4" /> Edit Profile
            </Button>
          </div>
        </section>

        {/* ===== Sea Service Record ===== */}
        <section className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="sea-service-section">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-lg font-extrabold text-gray-900">
              <Ship className="h-5 w-5 text-[#002855]" /> Sea Service Record
            </div>
            <div className="flex gap-2 text-xs font-bold">
              <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-600" data-testid="sea-total-contracts">{seaService.length} contract{seaService.length === 1 ? "" : "s"}</span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-[#002855]" data-testid="sea-total-time">{seaTime} at sea</span>
            </div>
            <Button onClick={() => openSea(null)} data-testid="sea-add-button"
              className="ml-auto rounded-full bg-[#002855] text-sm font-bold text-white hover:bg-[#001d40]">
              <Plus className="mr-1 h-4 w-4" /> Add Record
            </Button>
          </div>

          <div className="mt-4 space-y-3">
            {seaService.length === 0 && (
              <p className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-400" data-testid="sea-empty">
                No sea service yet — add your first contract to build your online resume.
              </p>
            )}
            {seaService.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-4 rounded-xl border border-gray-100 bg-gray-50/60 p-4" data-testid={`sea-record-${r.id}`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100">
                  <Anchor className="h-5 w-5 text-[#002855]" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-gray-900">{r.vessel_name}</span>
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-gray-600 border border-gray-200">{r.rank}</span>
                    {!r.sign_off && <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-bold text-green-700">Current</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                    {r.company && <span>{r.company}</span>}
                    {r.vessel_type && <span>· {r.vessel_type}</span>}
                    <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {r.sign_on} → {r.sign_off || "present"}</span>
                    <span className="font-bold text-[#002855]">{durationLabel(r.sign_on, r.sign_off)}</span>
                    {(r.grt || r.engine) && <span>· {[r.grt && `${r.grt} GRT`, r.engine].filter(Boolean).join(" / ")}</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openSea(r)} data-testid={`sea-edit-${r.id}`} className="rounded-lg p-2 text-gray-400 hover:bg-white hover:text-gray-700"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => deleteSea(r.id)} data-testid={`sea-delete-${r.id}`} className="rounded-lg p-2 text-gray-400 hover:bg-white hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ===== Certificate Vault ===== */}
        <section id="vault" className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="certificate-vault-section">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-lg font-extrabold text-gray-900">
              <Award className="h-5 w-5 text-[#002855]" /> Certificate Vault
            </div>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-600" data-testid="cert-count">{certs.length} doc{certs.length === 1 ? "" : "s"}</span>
            <Button onClick={() => openCert(null)} data-testid="cert-add-button"
              className="ml-auto rounded-full bg-[#FF4500] text-sm font-bold text-white hover:bg-[#e03d00]">
              <Plus className="mr-1 h-4 w-4" /> Add Certificate
            </Button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {certs.length === 0 && (
              <p className="col-span-full rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-400" data-testid="cert-empty">
                Upload your STCW certificates, CDC and medicals — expiry tracking is automatic.
              </p>
            )}
            {certs.map((c) => {
              const st = certStatus(c.expiry_date);
              const url = fileUrl(c);
              return (
                <div key={c.id} className="rounded-xl border border-gray-100 bg-gray-50/60 p-4" data-testid={`cert-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-2.5">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white border border-gray-200">
                        <FileText className="h-4 w-4 text-[#FF4500]" />
                      </span>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold text-gray-900">{c.name}</div>
                        {c.cert_number && <div className="text-xs text-gray-500">No. {c.cert_number}</div>}
                        <div className="mt-0.5 text-xs text-gray-400">
                          {c.issue_date || "—"} → {c.expiry_date || "no expiry"}
                        </div>
                      </div>
                    </div>
                    <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-1 border-t border-gray-200/70 pt-3">
                    {url ? (
                      <a href={url} target="_blank" rel="noreferrer" data-testid={`cert-view-${c.id}`}
                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-[#002855] hover:bg-white">
                        <Download className="h-3.5 w-3.5" /> {c.original_filename || "View file"}
                      </a>
                    ) : (
                      <span className="px-2 text-xs text-gray-400">No file attached</span>
                    )}
                    <div className="ml-auto flex gap-1">
                      <button onClick={() => openCert(c)} data-testid={`cert-edit-${c.id}`} className="rounded-lg p-1.5 text-gray-400 hover:bg-white hover:text-gray-700"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => deleteCert(c.id)} data-testid={`cert-delete-${c.id}`} className="rounded-lg p-1.5 text-gray-400 hover:bg-white hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* ===== Edit profile dialog ===== */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Edit Profile</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-name">Full name</Label>
              <Input id="p-name" data-testid="profile-name-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Rank</Label>
              <Select value={editForm.rank} onValueChange={(v) => setEditForm({ ...editForm, rank: v })}>
                <SelectTrigger data-testid="profile-rank-select"><SelectValue placeholder="Select rank" /></SelectTrigger>
                <SelectContent className="max-h-56">{rankOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Availability</Label>
              <Select value={editForm.availability} onValueChange={(v) => setEditForm({ ...editForm, availability: v })}>
                <SelectTrigger data-testid="availability-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(AVAILABILITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-phone">Phone <span className="text-xs text-gray-400">(so friends can find you)</span></Label>
              <Input id="p-phone" data-testid="profile-phone-input" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="9876543210" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-bio">Bio</Label>
              <textarea id="p-bio" data-testid="profile-bio-input" rows={3} value={editForm.bio}
                onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                placeholder="e.g. Sailing tankers since 2018. Looking for Chief Officer roles."
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002855]/30" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveProfile} disabled={saving} data-testid="profile-save-button"
              className="rounded-full bg-[#FF4500] font-bold text-white hover:bg-[#e03d00]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Sea service dialog ===== */}
      <Dialog open={seaOpen} onOpenChange={setSeaOpen}>
        <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{seaEditId ? "Edit" : "Add"} Sea Service Record</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="s-vessel">Vessel name *</Label>
              <Input id="s-vessel" data-testid="sea-vessel-input" value={seaForm.vessel_name} onChange={(e) => setSeaForm({ ...seaForm, vessel_name: e.target.value })} placeholder="MT Ocean Pioneer" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-company">Company</Label>
              <Input id="s-company" data-testid="sea-company-input" value={seaForm.company} onChange={(e) => setSeaForm({ ...seaForm, company: e.target.value })} placeholder="Synergy Marine" />
            </div>
            <div className="space-y-1.5">
              <Label>Rank *</Label>
              <Select value={seaForm.rank} onValueChange={(v) => setSeaForm({ ...seaForm, rank: v })}>
                <SelectTrigger data-testid="sea-rank-select"><SelectValue placeholder="Select rank" /></SelectTrigger>
                <SelectContent className="max-h-56">{rankOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Vessel type</Label>
              <Select value={seaForm.vessel_type} onValueChange={(v) => setSeaForm({ ...seaForm, vessel_type: v })}>
                <SelectTrigger data-testid="sea-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="max-h-56">{vesselOptions.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-on">Sign-on date *</Label>
              <Input id="s-on" data-testid="sea-sign-on-input" type="date" value={seaForm.sign_on} onChange={(e) => setSeaForm({ ...seaForm, sign_on: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-off">Sign-off date <span className="text-xs text-gray-400">(blank = current)</span></Label>
              <Input id="s-off" data-testid="sea-sign-off-input" type="date" value={seaForm.sign_off} onChange={(e) => setSeaForm({ ...seaForm, sign_off: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-grt">GRT <span className="text-xs text-gray-400">(optional)</span></Label>
              <Input id="s-grt" data-testid="sea-grt-input" value={seaForm.grt} onChange={(e) => setSeaForm({ ...seaForm, grt: e.target.value })} placeholder="48000" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-engine">Engine <span className="text-xs text-gray-400">(optional)</span></Label>
              <Input id="s-engine" data-testid="sea-engine-input" value={seaForm.engine} onChange={(e) => setSeaForm({ ...seaForm, engine: e.target.value })} placeholder="MAN B&W 6S60ME-C" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveSea} disabled={saving} data-testid="sea-save-button"
              className="rounded-full bg-[#002855] font-bold text-white hover:bg-[#001d40]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : seaEditId ? "Save changes" : "Add record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Certificate dialog ===== */}
      <Dialog open={certOpen} onOpenChange={setCertOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{certEditId ? "Edit" : "Add"} Certificate</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="c-name">Certificate name *</Label>
              <Input id="c-name" data-testid="cert-name-input" value={certForm.name} onChange={(e) => setCertForm({ ...certForm, name: e.target.value })} placeholder="Advanced Fire Fighting" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-number">Certificate number</Label>
              <Input id="c-number" data-testid="cert-number-input" value={certForm.cert_number} onChange={(e) => setCertForm({ ...certForm, cert_number: e.target.value })} placeholder="AFF-2025-1234" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="c-issue">Issue date</Label>
                <Input id="c-issue" data-testid="cert-issue-input" type="date" value={certForm.issue_date} onChange={(e) => setCertForm({ ...certForm, issue_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-expiry">Expiry date</Label>
                <Input id="c-expiry" data-testid="cert-expiry-input" type="date" value={certForm.expiry_date} onChange={(e) => setCertForm({ ...certForm, expiry_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-file">Attach file <span className="text-xs text-gray-400">(PDF / JPG / PNG, max 10 MB)</span></Label>
              <Input id="c-file" data-testid="cert-file-input" type="file" accept=".pdf,image/jpeg,image/png,image/webp"
                onChange={(e) => setCertForm({ ...certForm, file: e.target.files?.[0] || null })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveCert} disabled={saving} data-testid="cert-save-button"
              className="rounded-full bg-[#FF4500] font-bold text-white hover:bg-[#e03d00]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : certEditId ? "Save changes" : "Add certificate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
